import { useState, useRef, useCallback } from 'react'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import type { Thread } from '../lib/types'
import { useThreadStore } from '../stores/threadStore'
import ContextMenu from './ContextMenu'
import type { ContextMenuItem } from './ContextMenu'
import ConfirmDialog from './ConfirmDialog'
import ThreadTagManager from './ThreadTagManager'

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const secs = Math.floor(diff / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const SOURCE_ICONS: Record<string, { icon: React.ReactNode; label: string }> = {
  exchange: {
    label: 'Chat',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-11.25 4.5l-2.25 2.25V6.75A2.25 2.25 0 015.625 4.5h12.75a2.25 2.25 0 012.25 2.25v7.5a2.25 2.25 0 01-2.25 2.25H8.625z" />
      </svg>
    ),
  },
  youtube: {
    label: 'YouTube',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  blog: {
    label: 'Article',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A8.966 8.966 0 013 12c0-1.264.26-2.466.727-3.559" />
      </svg>
    ),
  },
  pdf: {
    label: 'PDF',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  notecard: {
    label: 'Note card',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
      </svg>
    ),
  },
}

// Swipe action threshold in pixels
const SWIPE_THRESHOLD = 80

export default function ThreadCard({ thread }: { thread: Thread }) {
  const navigate = useNavigate()
  const { deleteThread, pinThread, updateThread } = useThreadStore()

  // Context menu state
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 })
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(thread.title)
  const renameRef = useRef<HTMLInputElement>(null)
  const [tagsOpen, setTagsOpen] = useState(false)

  // Long-press detection
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchMoved = useRef(false)

  // Swipe state
  const x = useMotionValue(0)
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const swiping = useRef(false)

  // Swipe background colors
  const pinBg = useTransform(x, [0, SWIPE_THRESHOLD], ['rgba(212,168,67,0)', 'rgba(212,168,67,0.3)'])
  const deleteBg = useTransform(x, [-SWIPE_THRESHOLD, 0], ['rgba(239,68,68,0.3)', 'rgba(239,68,68,0)'])
  const pinOpacity = useTransform(x, [0, SWIPE_THRESHOLD * 0.5, SWIPE_THRESHOLD], [0, 0.5, 1])
  const deleteOpacity = useTransform(x, [-SWIPE_THRESHOLD, -SWIPE_THRESHOLD * 0.5, 0], [1, 0.5, 0])

  const contentTypes = thread.content_types || []

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchMoved.current = false
    const touch = e.touches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }

    longPressTimer.current = setTimeout(() => {
      if (!touchMoved.current) {
        navigator.vibrate?.(50)
        setMenuPos({ x: touch.clientX, y: touch.clientY })
        setMenuOpen(true)
      }
    }, 500)
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return
    const touch = e.touches[0]
    const dx = touch.clientX - touchStart.current.x
    const dy = touch.clientY - touchStart.current.y

    // If vertical movement is dominant, don't swipe
    if (!swiping.current && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
      clearLongPress()
      touchStart.current = null
      return
    }

    if (Math.abs(dx) > 10) {
      touchMoved.current = true
      clearLongPress()
      swiping.current = true
      x.set(dx)
    }
  }, [x, clearLongPress])

  const handleTouchEnd = useCallback(() => {
    clearLongPress()
    const currentX = x.get()

    if (currentX > SWIPE_THRESHOLD) {
      // Swipe right — pin/unpin
      animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 })
      pinThread(thread.id)
    } else if (currentX < -SWIPE_THRESHOLD) {
      // Swipe left — delete
      animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 })
      setConfirmDelete(true)
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 })
    }

    swiping.current = false
    touchStart.current = null
  }, [x, pinThread, thread.id, clearLongPress])

  // Right-click context menu for desktop
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setMenuPos({ x: e.clientX, y: e.clientY })
    setMenuOpen(true)
  }, [])

  const handleClick = useCallback(() => {
    if (touchMoved.current || menuOpen || renaming) return
    navigate(`/research/${thread.id}`)
  }, [navigate, thread.id, menuOpen, renaming])

  const handleDelete = useCallback(() => {
    setConfirmDelete(false)
    deleteThread(thread.id)
  }, [deleteThread, thread.id])

  const handleRename = useCallback(() => {
    setRenaming(true)
    setRenameValue(thread.title)
    setTimeout(() => renameRef.current?.focus(), 50)
  }, [thread.title])

  const commitRename = useCallback(() => {
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== thread.title) {
      updateThread(thread.id, { title: trimmed })
    }
    setRenaming(false)
  }, [renameValue, thread.id, thread.title, updateThread])

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      setRenaming(false)
    }
  }, [commitRename])

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: 'Rename',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
        </svg>
      ),
      onClick: handleRename,
    },
    {
      label: 'Tags',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
      ),
      onClick: () => setTagsOpen(true),
    },
    {
      label: thread.pinned ? 'Unpin' : 'Pin',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5V21m0-10.5l-3-3m3 3l3-3M12 3v4.5" />
        </svg>
      ),
      onClick: () => pinThread(thread.id),
    },
    {
      label: 'Delete',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
      ),
      onClick: () => setConfirmDelete(true),
      destructive: true,
    },
  ]

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl">
        {/* Swipe action backgrounds */}
        <motion.div
          className="absolute inset-0 flex items-center justify-start pl-5 rounded-2xl"
          style={{ backgroundColor: pinBg }}
        >
          <motion.div style={{ opacity: pinOpacity }}>
            <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5V21m0-10.5l-3-3m3 3l3-3M12 3v4.5" />
            </svg>
          </motion.div>
        </motion.div>
        <motion.div
          className="absolute inset-0 flex items-center justify-end pr-5 rounded-2xl"
          style={{ backgroundColor: deleteBg }}
        >
          <motion.div style={{ opacity: deleteOpacity }}>
            <svg className="w-6 h-6 text-error" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </motion.div>
        </motion.div>

        {/* Card body */}
        <motion.div
          className="relative bg-surface rounded-2xl p-4 cursor-pointer active:bg-elevated transition-colors"
          style={{ x }}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Title row */}
          <div className="flex items-center gap-1.5 min-w-0">
            {thread.pinned && (
              <svg className="w-3.5 h-3.5 text-accent shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 3a1 1 0 0 1 .707.293l4 4a1 1 0 0 1-.464 1.68l-2.02.505-.874.874 1.151 4.604a1 1 0 0 1-1.618.96L12 12.368l-4.882 3.548a1 1 0 0 1-1.618-.96l1.15-4.604-.873-.874-2.02-.505a1 1 0 0 1-.465-1.68l4-4A1 1 0 0 1 8 3h8z" />
              </svg>
            )}
            {renaming ? (
              <input
                ref={renameRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={handleRenameKeyDown}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 bg-bg border border-text-muted/30 rounded-lg px-2 py-1 text-sm font-medium text-text-primary outline-none focus:border-accent"
              />
            ) : (
              <h3 className="font-medium truncate text-text-primary text-[15px]">{thread.title}</h3>
            )}
            {thread.external_source === 'chatgpt' && (
              <span className="shrink-0 ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                Imported
              </span>
            )}
          </div>

          {/* Preview text */}
          {thread.preview && !renaming && (
            <p className="text-sm text-text-secondary truncate mt-1">{thread.preview}</p>
          )}

          {/* Personal tag pills */}
          {thread.tags && thread.tags.length > 0 && !renaming && (
            <div className="flex items-center gap-1 mt-1.5 overflow-hidden">
              {thread.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="shrink-0 px-2 py-0.5 rounded-full text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/30"
                >
                  {tag.name}
                </span>
              ))}
              {thread.tags.length > 3 && (
                <span className="shrink-0 text-[10px] text-text-muted">
                  +{thread.tags.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-1.5 text-xs text-text-muted">
            <span>{timeAgo(thread.updated_at)}</span>
            <span>{thread.exchange_count} messages</span>
            {contentTypes.length > 0 && (
              <div className="flex items-center gap-1 text-text-muted/70">
                {contentTypes.map((type) => {
                  const config = SOURCE_ICONS[type]
                  if (!config) return null
                  return (
                    <span key={type} title={config.label}>
                      {config.icon}
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Context menu */}
      <ContextMenu
        open={menuOpen}
        position={menuPos}
        items={contextMenuItems}
        onClose={() => setMenuOpen(false)}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this thread?"
        message="This thread and all its messages will be moved to trash."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* Tag manager overlay */}
      <ThreadTagManager
        open={tagsOpen}
        threadId={thread.id}
        onClose={() => setTagsOpen(false)}
      />
    </>
  )
}
